import { Form } from "@npco/zeller-design-system";
import { useForm, bindField } from "@npco/zeller-design-system/form";
import * as Input from "@npco/zeller-design-system/input";
import type { ADT } from "pb.adt";
import { z } from "zod";

import { logger } from "../../../shared/logger";
import { Layout } from "../shared/layout";
import type { Page } from "../terminal-setup";

const ValuesSchema = z.object({
	deviceName: z.string().max(256),
});

export function PageDeviceName({
	page: { deviceName, onNext, onBack },
	onClose,
	onLogout,
	pending,
}: {
	page: ADT.Pick<Page, "DeviceName">;
} & Required<Pick<Layout.Props, "onClose" | "onLogout" | "pending">>) {
	const form = useForm(
		ValuesSchema,
		async ({ value }) => {
			const response = await onNext(value.deviceName);
			if (response instanceof Error) {
				logger.error("setup: saving device name error", {
					errorName: response.name,
					errorMessage: response.message,
					errorStack: response.stack,
					errorCause: response.cause,
				});
				// `meta.errors` is derived from `errorMap`, so the error must be set
				// there — and as an object, since bindField surfaces
				// `errors[0]?.message`.
				form.setFieldMeta("deviceName", (field) => ({
					...field,
					errorMap: {
						...field.errorMap,
						onSubmit: { message: "Something went wrong." },
					},
				}));
				return;
			}
			return response;
		},
		{
			defaultValues: { deviceName },
		},
	);

	return (
		<form.Subscribe selector={(state) => state.isSubmitting}>
			{(isSubmitting) => (
				<Layout
					title="Name Device"
					progress
					progressValue={2}
					progressLimit={4}
					pending={pending || (isSubmitting && "submit")}
					onClose={onClose}
					onLogout={onLogout}
					actions={[
						{ label: "Next", onClick: form.handleSubmit, primary: true },
						onBack && { label: "Back", onClick: onBack },
					]}
				>
					<div className="flex flex-col gap-24 px-32 pt-6">
						<div className="text-lg text-grey-700 dark:text-grey-150">
							Enter the name of the device you are setting up (e.g. Front
							Counter).
						</div>
						<Form onSubmit={form.handleSubmit}>
							<form.Field
								name="deviceName"
								children={(field) => (
									<Input.Text
										{...bindField(field)}
										label="Device Name"
										$input={{ required: true, maxLength: 256 }}
									/>
								)}
							/>
						</Form>
					</div>
				</Layout>
			)}
		</form.Subscribe>
	);
}
